import { FC, JSXElementConstructor, ReactElement } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { CountryListProps } from '@elements/form';
import { ProductDetails } from '@store/proposal/types';
import {
  AutocompleteProps,
  MultiSelectProps,
  TextInputProps,
  NumberInputProps,
  SelectProps,
  RadioGroupProps,
  TextProps,
  FlexProps,
  DateInputProps,
  TextareaProps,
  MonthPickerInputProps
} from '@ui';

import { DittoAlertProps } from '../widgets';

import { SwitchFormWrapperProps } from './AdaptiveNestedSwitchForm';
import {
  AccordionSectionProps,
  AsyncAutocompleteProps,
  CardSelectProps,
  ChipSelectProps,
  SliderCoverPickerProps,
  MembersAccordionProps,
  SwitcherQuestionProps,
  NomineeSelectProps,
  MemberDetailsProps,
  ArrayFormFieldsProps,
  PhoneNumberFieldWithContextProps,
  ButtonSelectProps,
  CustomSliderProps,
  CoverPickerProps,
  HeightPickerProps,
  ErrorFieldProps
} from './custom-fields';

export enum Fields {
  text = 'text',
  textarea = 'textarea',
  number = 'number',
  date = 'date',
  month = 'month',
  switch = 'switch',
  arrayForm = 'arrayForm',
  autocomplete = 'autocomplete',
  accordionForm = 'accordionForm',
  accordionSection = 'accordionSection',
  multiSelectAutocomplete = 'multiSelectAutocomplete',
  sectionContainer = 'sectionContainer',
  columnsContainer = 'columnsContainer',
  section = 'section',
  divider = 'divider',
  select = 'select',
  sectionTitle = 'sectionTitle',
  title = 'title',
  custom = 'custom',
  error = 'error',
  switchGroup = 'switchGroup',
  checkboxGroup = 'checkboxGroup',
  heightPicker = 'heightPicker',
  alert = 'alert',
  radioCheckboxes = 'radioCheckboxes',
  checkbox = 'checkbox',
  intlPhoneNumber = 'intlPhoneNumber',
  countryList = 'countryList',
  asyncAutocomplete = 'asyncAutocomplete',
  sliderCoverPicker = 'sliderCoverPicker',
  customSlider = 'customSlider',
  coverPicker = 'coverPicker',
  cardSelect = 'cardSelect',
  radioGroup = 'radioGroup',
  chipSelect = 'chipSelect',
  buttonSelect = 'buttonSelect',
  membersAccordion = 'membersAccordion',
  nomineeSelect = 'nomineeSelect',
  memberDetails = 'memberDetails'
}

export type CheckboxConfig = FormField<Fields.checkbox, any>;

export type NestableCheckboxConfig = NestableFormField<Fields.checkbox, any>;

export const FieldDataTypes = {
  array: 'array'
} as const;

type TFieldDataTypes = typeof FieldDataTypes;

type FormField<type, config> = {
  type: type;
  componentProps?: Omit<config, 'name' | 'label'> & object;
  setComponentProps?: (
    getValues: UseFormReturn['getValues'],
    productDetails?: ProductDetails
  ) => Omit<config, 'name' | 'label'>;
} & (
  | { componentProps: Omit<config, 'name' | 'label'> & object }
  | {
      setComponentProps: (
        getValues: UseFormReturn['getValues'],
        productDetails?: ProductDetails
      ) => Omit<config, 'name' | 'label'>;
    }
);

type NestableFormField<type, config> = FormField<type, config> & {
  fields: Array<any>;
  wrapperStyle?: React.CSSProperties;
};

/**
 *************************
 *************************
 *** Base Field Config ***
 *************************
 *************************
 */
export type AdaptiveFormFieldConfigBase = {
  // name of the field
  name: string;
  // label of the field
  label: string | ReactElement<any, string | JSXElementConstructor<any>>;
  summaryLabel?: string; // label to be used in the summary view (ex: when actual label is too long)
  // callback to check if the field should be shown or not. The callback will receive the current value of the form
  // and the productDetails object
  condition?: (
    watchedValue: any,
    productDetails: ProductDetails | undefined
  ) => boolean;
  // field spans 12 columns
  fullRow?: boolean;
  // form configuration for fields that support nesting
  fields?: AdaptiveFormConfig;
  // callback to get the form configuration for fields that support nesting
  getFields?: (memberIndex: number) => AdaptiveFormConfig;
  // next field will be in a new row
  sectionBreak?: boolean;
  // grid props for the field
  gridProps?: FlexProps;
  // set the dataType of custom fields to 'array'
  dataType?: keyof TFieldDataTypes;
  // used when rendering summary of array like fields
  index?: number;
  // This value will be used in the summary view if the field value is falsy
  fallbackForSummary?: string;
  // render the field but hide it in the step
  hidden?: boolean;
  // This string will be used as the label
  // Use this if the label has popup
  fallbackLabelForSummary?: string;
  // styles applied to the outer FieldWrapper box (e.g. alignSelf to control vertical alignment)
  wrapperStyle?: React.CSSProperties;
};

/**
 *****************************************************************************
 *   Type of Fields like text, date, switch, etc
 *   Some of these fields are nestable and some are not.
 *   Nestable fields are those which can have children fields like accordion, arrayForm, etc.
 *****************************************************************************
 */
export type TextFieldConfig = FormField<Fields.text, TextInputProps>;
export type TextareaFieldConfig = FormField<Fields.textarea, TextareaProps>;

export type NumberFieldConfig = FormField<Fields.number, NumberInputProps>;

export type DateFieldConfig = FormField<Fields.date, DateInputProps>;

export type MonthFieldConfig = FormField<
  Fields.month,
  MonthPickerInputProps<'default'>
>;

export type AutoCompleteFieldConfig = FormField<
  Fields.autocomplete,
  Partial<AutocompleteProps>
>;

export type AsyncAutoCompleteFieldConfig = FormField<
  Fields.asyncAutocomplete,
  Partial<AsyncAutocompleteProps>
>;

export type MultiAutoCompleteFieldConfig = FormField<
  Fields.multiSelectAutocomplete,
  MultiSelectProps
>;

// todo : need to modify phone number select group to use custom country list
export type IntlPhoneNumberFieldConfig = FormField<
  Fields.intlPhoneNumber,
  PhoneNumberFieldWithContextProps
>;

export type CountryListFieldConfig = FormField<Fields.countryList, CountryListProps>;

export type SwitchFieldConfig = NestableFormField<
  Fields.switch,
  SwitcherQuestionProps
> &
  Omit<SwitchFormWrapperProps, 'config' | 'children'>;

export type SwitchGroupConfig = NestableFormField<
  Fields.switchGroup,
  {
    touchedField: string;
  }
>;

export type CheckboxGroupConfig = NestableFormField<
  Fields.checkboxGroup,
  {
    touchedField: string;
  }
>;

export type SelectFieldConfig = FormField<
  Fields.select,
  SelectProps & {
    // summaryViewValueMapping is used to map the value to a different value
    // It should be set in the field componentProps, cannot be set using setComponentProps
    summaryViewValueMapping?: any;
  }
>;

export type FieldErrorConfig = FormField<Fields.error, ErrorFieldProps>;

type AccordionFormFieldConfig = NestableFormField<
  Fields.accordionForm,
  AccordionSectionProps
>;

export type ArrayFormFieldConfig = NestableFormField<
  Fields.arrayForm,
  Omit<ArrayFormFieldsProps, 'formConfig'> & {
    // Render the member name in the summary view
    showMemberAsTitle?: boolean;
  }
>;

export type SliderCoverPickerFieldConfig = FormField<
  Fields.sliderCoverPicker,
  Partial<SliderCoverPickerProps>
>;

export type CustomSliderFieldConfig = FormField<
  Fields.customSlider,
  Partial<CustomSliderProps>
>;

export type CoverPickerFieldConfig = FormField<
  Fields.coverPicker,
  Partial<CoverPickerProps>
>;

export type AccordionSectionFieldConfig = FormField<
  Fields.accordionSection,
  Partial<AccordionSectionProps>
>;

export type CardSelectFieldConfig = FormField<
  Fields.cardSelect,
  Partial<CardSelectProps>
>;

export type CustomFieldRenderer<T = any> = FC<{
  config: AdaptiveFormConfig;
  componentProps: T;
  context?: any;
}>;

export type CustomFieldConfig = NestableFormField<Fields.custom, any> & {
  Render: CustomFieldRenderer;
  /** Each custom field can resolve its summary display value differently since they store data in non-standard paths */
  getSummaryValue?: (data: any, componentProps: any) => string | null;
};

export type SectionContainerType = NestableFormField<Fields.sectionContainer, any>;

export type ColumnsContainerType = NestableFormField<
  Fields.columnsContainer,
  { columns: number }
>;

export type DividerType = FormField<Fields.divider, any>;

export type SectionTitle = FormField<
  Fields.sectionTitle,
  {
    primary: string;
    secondary?: string;
    showForSummary?: boolean;
    primaryProps?: TextProps;
    secondaryProps?: TextProps;
    containerProps?: FlexProps;
  }
>;

export type SectionType = NestableFormField<Fields.section, any> & {
  componentProps?: {
    title: string;
    subtitle: string;
  };
};

export type Alert = FormField<Fields.alert, DittoAlertProps>;

export type MemberDetailsType = FormField<Fields.memberDetails, MemberDetailsProps>;

export type Title = FormField<
  Fields.title,
  {
    primary: string;
    secondary?: string;
    showForSummary?: boolean;
  }
>;

export type MembersAccordionConfig = FormField<
  Fields.membersAccordion,
  Omit<MembersAccordionProps, 'getSubQuestionsConfig'> & {
    showSimplifiedSummary?: boolean;
  }
>;

export type RadioCheckboxesConfig = FormField<Fields.radioCheckboxes, any> & {
  parentFieldName: string;
};

export type NomineeSelectConfig = NestableFormField<
  Fields.nomineeSelect,
  Omit<NomineeSelectProps, 'fields' | 'name'>
>;

export type HeightPickerFieldConfig = FormField<
  Fields.heightPicker,
  Omit<HeightPickerProps, 'ftName' | 'inName'>
> & {
  ftName: string;
  inName: string;
};

export type RadioGroupFieldConfig = FormField<Fields.radioGroup, RadioGroupProps>;

export type ChipSelectFieldConfig = FormField<Fields.chipSelect, ChipSelectProps>;

export type ButtonSelectFieldConfig = FormField<
  Fields.buttonSelect,
  ButtonSelectProps
>;

/**
 *************************
 *************************
 ***Non nestable Fields***
 *************************
 *************************
 */
export type AdaptiveFormNonNestableFieldConfig =
  | (Omit<CustomFieldConfig, 'fields'> & Partial<AdaptiveFormFieldConfigBase>)
  | (Partial<AdaptiveFormFieldConfigBase> &
      (
        | TextFieldConfig
        | TextareaFieldConfig
        | NumberFieldConfig
        | DateFieldConfig
        | MonthFieldConfig
        | AutoCompleteFieldConfig
        | MultiAutoCompleteFieldConfig
        | Omit<SwitchFieldConfig, 'fields'>
        | SelectFieldConfig
        | FieldErrorConfig
        | RadioCheckboxesConfig
        | CheckboxConfig
        | MembersAccordionConfig
        | NomineeSelectConfig
        | HeightPickerFieldConfig
        | IntlPhoneNumberFieldConfig
        | CountryListFieldConfig
        | TextareaFieldConfig
        | AsyncAutoCompleteFieldConfig
        | SliderCoverPickerFieldConfig
        | CoverPickerFieldConfig
        | CustomSliderFieldConfig
        | CardSelectFieldConfig
        | RadioGroupFieldConfig
        | ChipSelectFieldConfig
        | ButtonSelectFieldConfig
      ));

export type AdaptiveFormNestableFieldConfig = Partial<AdaptiveFormFieldConfigBase> &
  (
    | SwitchFieldConfig
    | AccordionFormFieldConfig
    | ArrayFormFieldConfig
    | NestableCheckboxConfig
  );

export type AdaptiveNonFormDataFields =
  | (Partial<AdaptiveFormFieldConfigBase> &
      (
        | SectionContainerType
        | ColumnsContainerType
        | DividerType
        | SectionTitle
        | Alert
        | Title
        | CustomFieldConfig
        | SectionType
        | MemberDetailsType
      ))
  | (Partial<AdaptiveFormFieldConfigBase> & SwitchGroupConfig)
  | (Partial<AdaptiveFormFieldConfigBase> & CheckboxGroupConfig)
  | (Partial<AdaptiveFormFieldConfigBase> & AccordionSectionFieldConfig);

export type AdaptiveFormFieldConfig =
  | AdaptiveNonFormDataFields
  | AdaptiveFormNonNestableFieldConfig
  | AdaptiveFormNestableFieldConfig;

export type AdaptiveNestableFormConfig = Record<
  string,
  AdaptiveFormNestableFieldConfig
>;

export type AdaptiveNonNestableFormConfig = Record<
  string,
  AdaptiveFormNonNestableFieldConfig
>;

export type AdaptiveFormConfig = AdaptiveFormFieldConfig[];

export type AdaptiveFormProps = {
  config: AdaptiveFormConfig;
  loading?: boolean;
  submitButtonText?: string;
};
